import React, { useState } from "react";
import { Box, Typography, FormControlLabel, Checkbox, TextField, MenuItem } from "@mui/material";
import { gql, useQuery } from "@apollo/client";

// Define the GraphQL query outside the component
const EANS_BY_ADDRESS_QUERY = gql`
    query EansByAddress ($request:EanByAddressRequestInput!) {
    eansByAddress(request: $request) {
        isSuccess
        message
        value {
            meteringPoints {
                ean
                address {
                    postalCode
                    street
                    streetNumber
                    city
                }
                product
                organisation
                gridOperatorEan
                specialMeteringPoint
                gridArea
            }
        }
    }
    }
`;

const externalDataProviders = [
    { value: "NONE", label: "None" },
    { value: "EDSN", label: "EDSN" },
    { value: "FUDURA", label: "Fudura" },
    { value: "EVA_MEET", label: "EvaMeet" },
    { value: "MEET_DIRECT", label: "MeetDirect" },
    { value: "ANEXO", label: "Anexo" },
    { value: "INNAX", label: "INNAX" },
    { value: "JOULZ", label: "Joulz" },
    { value: "KENTER", label: "Kenter" },
    { value: "TUMS", label: "TUMS" },
    { value: "HET_MEETBEDRIJF", label: "Het Meetbedrijf" },
];

const MeterSelectionBox = ({
    onMetersChange,
    postalCode,
    streetNumber,
    streetNumberAddition,
}) => {
    const addition = streetNumberAddition == null ? "" : streetNumberAddition;

    const { data, loading, error } = useQuery(EANS_BY_ADDRESS_QUERY, {
        variables: {
            request: {
                postalCode,
                streetNumber,
                addition: addition
            }
        },
        skip: !postalCode || !streetNumber,
    });

    // State for selected meters (by EAN)
    const [selectedMeters, setSelectedMeters] = useState([]);
    // State for additional form data per meter (by EAN)
    const [meterFormData, setMeterFormData] = useState({});

    const getProductName = (product) => {
        if (product === "ELK") return "ELECTRICITY";
        if (product === "GAS") return "GAS";
        return product;
    };

    // Helper to build the full meter objects array
    const buildMetersArray = (selected, formData) => {
        return selected.map(ean => {
            const meter = (meteringPoints || []).find(m => m.ean === ean) || {};
            const userFields = formData[ean] || {};
            return {
                // Backend expects these fields:
                name: meter.ean || "", // or a better name if available
                ean18: meter.ean || "",
                buildingId: meter.buildingId || "", // You may need to set this from parent/building context
                gridOperator: "STEDIN",
                meterType: meter.product ? (meter.product === "ELK" ? "ELECTRICITY" : meter.product) : "",
                lowRate: Number(userFields.lowRate) || 0,
                highRate: Number(userFields.highRate) || 0,
                contractedCapacity: Number(userFields.contractedCapacity) || 0,
                externalDataProvider: userFields.externalDataProvider || "NONE",
            };
        });
    };

    // Handle checkbox selection
    const handleMeterSelection = (ean) => {
        let updated;
        if (selectedMeters.includes(ean)) {
            updated = selectedMeters.filter((id) => id !== ean);
            setSelectedMeters(updated);
            setMeterFormData(prev => {
                const { [ean]: _, ...rest } = prev;
                if (onMetersChange) {
                    onMetersChange(buildMetersArray(updated, rest));
                }
                return rest;
            });
        } else {
            updated = [...selectedMeters, ean];
            const meter = (meteringPoints || []).find(m => m.ean === ean);
            setSelectedMeters(updated);
            setMeterFormData(prev => {
                const existing = prev[ean] || {};
                const extraFields = {};
                if (meter) {
                    const textFieldKeys = ["lowRate", "highRate", "contractedCapacity", "externalDataProvider"];
                    Object.keys(meter).forEach(key => {
                        if (!textFieldKeys.includes(key)) {
                            extraFields[key] = meter[key];
                        }
                    });
                    if (meter.address) {
                        extraFields.address = meter.address;
                    }
                }
                const updatedFormData = {
                    ...prev,
                    [ean]: {
                        ...extraFields,
                        ...existing
                    }
                };
                if (onMetersChange) {
                    onMetersChange(buildMetersArray(updated, updatedFormData));
                }
                return updatedFormData;
            });
        }
    };

    // Handle changes in extra fields
    const handleMeterFieldChange = (ean, field, value) => {
        setMeterFormData(prev => {
            const updated = {
                ...prev,
                [ean]: {
                    ...prev[ean],
                    [field]: value
                }
            };
            if (onMetersChange) {
                onMetersChange(buildMetersArray(selectedMeters, updated));
            }
            return updated;
        });
    };

    if (loading) return <Typography>Loading...</Typography>;
    if (error) return <Typography color="error">Error: {error.message}</Typography>;

    const meteringPoints = data?.eansByAddress?.value?.meteringPoints || [];

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6">Select Meters</Typography>
            {meteringPoints.length === 0 && (
                <Typography color="text.secondary">No meters found for this address.</Typography>
            )}
            {meteringPoints.map((meter) => (
                <Box key={meter.ean} sx={{ mb: 2, border: "1px solid #eee", borderRadius: 1, p: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedMeters.includes(meter.ean)}
                                onChange={() => handleMeterSelection(meter.ean)}
                            />
                        }
                        label={
                            <>
                                {meter.ean} ({getProductName(meter.product)})<br />
                                <Typography variant="caption" component="span">
                                    {meter.address.street} {meter.address.streetNumber} {meter.address.city}
                                </Typography>
                                <br />
                                <Typography variant="caption" component="span">
                                    {meter.organisation}
                                    {meter.gridOperatorEan ? ` ( ${meter.gridOperatorEan})` : ""}
                                </Typography>
                            </>
                        }
                    />
                    {selectedMeters.includes(meter.ean) && (
                        <Box sx={{ mt: 1, ml: 4 }}>
                            <TextField
                                label="Low Rate"
                                type="number"
                                value={meterFormData[meter.ean]?.lowRate || ""}
                                onChange={e => handleMeterFieldChange(meter.ean, "lowRate", e.target.value)}
                                fullWidth
                                margin="dense"
                            />
                            <TextField
                                label="High Rate"
                                type="number"
                                value={meterFormData[meter.ean]?.highRate || ""}
                                onChange={e => handleMeterFieldChange(meter.ean, "highRate", e.target.value)}
                                fullWidth
                                margin="dense"
                            />
                            <TextField
                                label="Contracted Capacity"
                                type="number"
                                value={meterFormData[meter.ean]?.contractedCapacity || ""}
                                onChange={e => handleMeterFieldChange(meter.ean, "contractedCapacity", e.target.value)}
                                fullWidth
                                margin="dense"
                            />
                            <TextField
                                select
                                label="External Data Provider"
                                value={meterFormData[meter.ean]?.externalDataProvider || ""}
                                onChange={e => handleMeterFieldChange(meter.ean, "externalDataProvider", e.target.value)}
                                fullWidth
                                margin="dense"
                            >
                                {externalDataProviders.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    )}
                </Box>
            ))}
        </Box>
    );
};

export default MeterSelectionBox;